import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Dashboard from '@pages/Dashboard';
import { ForgotPassword, ResetPassword } from './pages/PasswordReset';
import AccountingExports from './pages/AccountingExports';
import Login from './pages/Login';
import ForcePasswordChange from './pages/ForcePasswordChange';
import Profile from './pages/Profile';
import Catalog from './pages/Catalog';
import ProductDetail from './pages/ProductDetail';
import OrderCreate from './pages/OrderCreate';
import OrdersList from './pages/OrdersList';
import Documents from './pages/Documents';
import OrderDetail from './pages/OrderDetail';
import { QuotesList, QuoteDetail } from './pages/Quotes';
import DiscountRequests from './pages/DiscountRequests';
import ActionQueue from './pages/ActionQueue';
import StockAlerts from './pages/StockAlerts';
import ZoneInventory from './pages/ZoneInventory';
import WarehouseDashboard from './pages/WarehouseDashboard';
import ReceivingTablet from './pages/ReceivingTablet';
import AnomaliesPage from './pages/AnomaliesPage';
import CallListPage from './pages/CallListPage';
import BinsPage from './pages/BinsPage';
import Picking from './pages/Picking';
import Shelving from './pages/Shelving';
import Quarantine from './pages/Quarantine';
import Statement from './pages/Statement';
import SupplierReturns from './pages/SupplierReturns';
import SupplierInvoiceOcr from './pages/SupplierInvoiceOcr';
import Invoices from './pages/Invoices';
import RecapInvoices from './pages/RecapInvoices';
import Remittance from './pages/Remittance';
import RecapTracking from './pages/RecapTracking';
import CollectionOverview from './pages/collection/CollectionOverview';
import CollectionPayments from './pages/collection/CollectionPayments';
import CollectionDunning from './pages/collection/CollectionDunning';
import CollectionAccounts from './pages/collection/CollectionAccounts';
import CollectionAccount from './pages/collection/CollectionAccount';
import CollectionSettings from './pages/collection/CollectionSettings';
import CreditNotes from './pages/CreditNotes';
import Returns from './pages/Returns';
import Refunds from './pages/Refunds';
import EanPrep from './pages/EanPrep';
import PosClose from './pages/PosClose';
import Replenishment from './pages/Replenishment';
import Comparison from './pages/Comparison';
import StockImport from './pages/StockImport';
import PurchasesRegister from './pages/PurchasesRegister';
import PurchaseImport from './pages/PurchaseImport';
import Settings from './pages/admin/Settings';
import DocumentTemplates from './pages/admin/DocumentTemplates';
import CounterTabs from './pages/admin/CounterTabs';
import Audit from './pages/admin/Audit';
import B2bUsers from './pages/admin/B2bUsers';
import Users from './pages/admin/Users';
import ProductManagement from './pages/admin/ProductManagement';
import CategoryTree from './pages/admin/CategoryTree';
import PricingGrids from './pages/admin/PricingGrids';
import Brands from './pages/admin/Brands';
import AdminSuppliers from './pages/admin/Suppliers';
import Customers from './pages/Customers';
import CustomerCreate from './pages/CustomerCreate';
import RelationDetail from './pages/RelationDetail';
import Notifications from './pages/Notifications';
import Stub from './pages/Stub';
import SupplierOrders from './pages/SupplierOrders';
import RevenueDashboard from './pages/reports/RevenueDashboard';
import InvoiceBatchValidation from './pages/einvoice/InvoiceBatchValidation';
import VatReconciliation from './pages/einvoice/VatReconciliation';
import EInvoiceMonitoring from './pages/einvoice/Monitoring';
import { AppShell } from './components/AppShell';
import ReturnsList from './pages/returns/ReturnsList';
import ReturnNew from './pages/returns/ReturnNew';
import ReturnDetail from './pages/returns/ReturnDetail';
import PortalReturns from './pages/returns/PortalReturns';
import { StaffReturnsRoute } from './components/returns/returnsShared';
import { ReturnsMoney, ReturnsRules, ReturnsSuppliers } from './pages/returns/ReturnsWork';
import { CustomerShell } from './customer/CustomerShell';
import CustomerCatalog from './customer/CustomerCatalog';
import CustomerAccount from './customer/CustomerAccount';
import { isCustomerRole } from './customer/shared';
import { ToastProvider } from './components/ui/Toast';
import { OfflineBanner } from './components/ui/OfflineBanner';
import { useAuthStore } from './stores/authStore';
import { useCapabilitiesStore } from './stores/capabilitiesStore';
import './index.css';
import './styles/mds.css';

/** Shown while /me/capabilities is in flight — before any shell has mounted. */
function LoadingScreen() {
  const { t } = useTranslation();
  return <div className="p-6 text-muted-foreground">{t('common.loading')}</div>;
}

function Landing() {
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const caps = useCapabilitiesStore((s) => s.data);
  const loaded = useCapabilitiesStore((s) => s.loaded);
  if (!isAuthed) return <Navigate to="/login" replace />;
  if (!loaded || !caps) return <LoadingScreen />;
  return <Navigate to={caps.navigation.landingPath} replace />;
}

function Protected({ children }: { children: React.ReactNode }) {
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const loaded = useCapabilitiesStore((s) => s.loaded);
  if (!isAuthed) return <Navigate to="/login" replace />;
  if (!loaded) return <LoadingScreen />;
  // Customer-type accounts get the PartsTech-style portal shell; staff get the admin AppShell.
  if (isCustomerRole(role)) return <CustomerShell>{children}</CustomerShell>;
  return <AppShell>{children}</AppShell>;
}

function CapabilitiesLoader() {
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const loaded = useCapabilitiesStore((s) => s.loaded);
  const loading = useCapabilitiesStore((s) => s.loading);
  const fetchCaps = useCapabilitiesStore((s) => s.fetch);
  useEffect(() => {
    if (isAuthed && !loaded && !loading) void fetchCaps();
  }, [isAuthed, loaded, loading, fetchCaps]);
  return null;
}

function App() {
  return (
    <ToastProvider>
      <OfflineBanner />
      <Router>
        <CapabilitiesLoader />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ForcePasswordChange />} />
          {/* Self-service "Mot de passe oublié ?" (2026-10-23): public, token-guarded. */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Landing />} />
          <Route
            path="/dashboard"
            element={
              <Protected>
                <Dashboard />
              </Protected>
            }
          />
          <Route
            path="/profile"
            element={
              <Protected>
                <Profile />
              </Protected>
            }
          />
          <Route
            path="/notifications"
            element={
              <Protected>
                <Notifications />
              </Protected>
            }
          />

          {/* Catalog */}
          <Route
            path="/catalog"
            element={
              <Protected>
                <Catalog />
              </Protected>
            }
          />
          <Route
            path="/catalog/:id"
            element={
              <Protected>
                <ProductDetail />
              </Protected>
            }
          />
          <Route
            path="/products"
            element={
              <Protected>
                <ProductManagement />
              </Protected>
            }
          />
          <Route
            path="/products/categories"
            element={
              <Protected>
                <CategoryTree />
              </Protected>
            }
          />

          {/* Orders + quotes */}
          {/* /orders is the unified document list: the counter works from one searchable list,
              not eight per-family screens. OrdersList survives for the POS Tickets view, which
              is deliberately a single-channel list. */}
          <Route
            path="/orders"
            element={
              <Protected>
                <Documents />
              </Protected>
            }
          />
          <Route
            path="/documents"
            element={
              <Protected>
                <Documents />
              </Protected>
            }
          />
          <Route
            path="/orders/new"
            element={
              <Protected>
                <OrderCreate />
              </Protected>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <Protected>
                <OrderDetail />
              </Protected>
            }
          />
          <Route
            path="/b2b/orders"
            element={
              <Protected>
                <OrdersList />
              </Protected>
            }
          />
          <Route
            path="/b2b/catalog"
            element={
              <Protected>
                <CustomerCatalog />
              </Protected>
            }
          />
          <Route
            path="/quotes"
            element={
              <Protected>
                <QuotesList />
              </Protected>
            }
          />
          <Route
            path="/quotes/:id"
            element={
              <Protected>
                <QuoteDetail />
              </Protected>
            }
          />

          {/* Discounts */}
          <Route
            path="/discounts"
            element={
              <Protected>
                <DiscountRequests />
              </Protected>
            }
          />
          <Route
            path="/discount-requests"
            element={
              <Protected>
                <DiscountRequests />
              </Protected>
            }
          />
          <Route
            path="/discounts/pending"
            element={
              <Protected>
                <DiscountRequests />
              </Protected>
            }
          />

          {/* Guide E12 — the "action required" queue, for the buyer and the counter alike. */}
          <Route
            path="/action-queue"
            element={
              <Protected>
                <ActionQueue />
              </Protected>
            }
          />

          {/* Stock / warehouse */}
          <Route
            path="/stock"
            element={
              <Protected>
                <StockAlerts />
              </Protected>
            }
          />
          <Route
            path="/stock/alerts"
            element={
              <Protected>
                <StockAlerts />
              </Protected>
            }
          />
          <Route
            path="/warehouse"
            element={
              <Protected>
                <WarehouseDashboard />
              </Protected>
            }
          />
          <Route
            path="/warehouse/dashboard"
            element={
              <Protected>
                <WarehouseDashboard />
              </Protected>
            }
          />
          {/* Receiving, bins and anomalies (spec of 2026-09-23): the tablet replaces the one-pass
              reception; the by-purchase-order view is one of its tabs. */}
          <Route
            path="/warehouse/reception"
            element={
              <Protected>
                <ReceivingTablet />
              </Protected>
            }
          />
          <Route
            path="/warehouse/ean-prep"
            element={
              <Protected>
                <EanPrep />
              </Protected>
            }
          />
          <Route
            path="/warehouse/bins"
            element={
              <Protected>
                <BinsPage />
              </Protected>
            }
          />
          <Route
            path="/counter/call-list"
            element={
              <Protected>
                <CallListPage />
              </Protected>
            }
          />
          <Route
            path="/anomalies"
            element={
              <Protected>
                <AnomaliesPage />
              </Protected>
            }
          />
          <Route
            path="/warehouse/picking"
            element={
              <Protected>
                <Picking />
              </Protected>
            }
          />
          {/* M7 / SC-02 step 10a — STEP 2 of the two-step reception. */}
          <Route
            path="/warehouse/shelving"
            element={
              <Protected>
                <Shelving />
              </Protected>
            }
          />
          <Route
            path="/warehouse/shipping"
            element={
              <Protected>
                <Stub k="shipping" />
              </Protected>
            }
          />
          <Route
            path="/warehouse/inventory"
            element={
              <Protected>
                <ZoneInventory />
              </Protected>
            }
          />
          <Route
            path="/warehouse/quarantine"
            element={
              <Protected>
                <Quarantine />
              </Protected>
            }
          />

          {/* J7 — statement of account. With :id it is the ACCOUNTING view of one relation;
              without it, the B2B portal's own statement (customerId comes from the JWT). */}
          <Route
            path="/customers/:id/statement"
            element={
              <Protected>
                <Statement />
              </Protected>
            }
          />
          <Route
            path="/account/statement"
            element={
              <Protected>
                <Statement />
              </Protected>
            }
          />

          {/* Invoices / returns */}
          <Route
            path="/invoices"
            element={
              <Protected>
                <Invoices />
              </Protected>
            }
          />
          <Route
            path="/b2b/invoices"
            element={
              <Protected>
                <Invoices />
              </Protected>
            }
          />
          <Route
            path="/recap-invoices"
            element={
              <Protected>
                <RecapInvoices />
              </Protected>
            }
          />
          <Route
            path="/recap-tracking"
            element={
              <Protected>
                <RecapTracking />
              </Protected>
            }
          />
          <Route
            path="/remittance"
            element={
              <Protected>
                <Remittance />
              </Protected>
            }
          />
          <Route
            path="/credit-notes"
            element={
              <Protected>
                <CreditNotes />
              </Protected>
            }
          />
          {/* Receivables collection (migration 151) — the owner's Recouvrement prototype. */}
          <Route path="/payments" element={<Navigate to="/collection/payments" replace />} />
          <Route
            path="/collection"
            element={
              <Protected>
                <CollectionOverview />
              </Protected>
            }
          />
          <Route
            path="/collection/payments"
            element={
              <Protected>
                <CollectionPayments />
              </Protected>
            }
          />
          <Route
            path="/collection/dunning"
            element={
              <Protected>
                <CollectionDunning />
              </Protected>
            }
          />
          <Route
            path="/collection/accounts"
            element={
              <Protected>
                <CollectionAccounts />
              </Protected>
            }
          />
          <Route
            path="/collection/accounts/:id"
            element={
              <Protected>
                <CollectionAccount />
              </Protected>
            }
          />
          <Route
            path="/collection/settings"
            element={
              <Protected>
                <CollectionSettings />
              </Protected>
            }
          />
          <Route
            path="/accounting/exports"
            element={
              <Protected>
                <AccountingExports />
              </Protected>
            }
          />
          <Route
            path="/returns"
            element={
              <Protected>
                <StaffReturnsRoute>
                  <ReturnsList />
                </StaffReturnsRoute>
              </Protected>
            }
          />
          <Route
            path="/returns/new"
            element={
              <Protected>
                <StaffReturnsRoute>
                  <ReturnNew />
                </StaffReturnsRoute>
              </Protected>
            }
          />
          {/* Returns v2: reception and validations are tabs of /returns (2026-10-16). */}
          <Route
            path="/returns/reception"
            element={<Navigate to="/returns?tab=RECEPTION" replace />}
          />
          {/* Returns v2: reception and validations are tabs of /returns (2026-10-16). */}
          <Route
            path="/returns/validations"
            element={<Navigate to="/returns?tab=VALIDATIONS" replace />}
          />
          <Route
            path="/returns/suppliers"
            element={
              <Protected>
                <StaffReturnsRoute>
                  <ReturnsSuppliers />
                </StaffReturnsRoute>
              </Protected>
            }
          />
          <Route
            path="/returns/money"
            element={
              <Protected>
                <StaffReturnsRoute>
                  <ReturnsMoney />
                </StaffReturnsRoute>
              </Protected>
            }
          />
          <Route
            path="/returns/rules"
            element={
              <Protected>
                <StaffReturnsRoute>
                  <ReturnsRules />
                </StaffReturnsRoute>
              </Protected>
            }
          />
          <Route
            path="/returns/legacy"
            element={
              <Protected>
                <Returns />
              </Protected>
            }
          />
          <Route
            path="/returns/:id"
            element={
              <Protected>
                <StaffReturnsRoute>
                  <ReturnDetail />
                </StaffReturnsRoute>
              </Protected>
            }
          />
          <Route
            path="/b2b/returns"
            element={
              <Protected>
                <PortalReturns />
              </Protected>
            }
          />
          <Route
            path="/b2b/returns/new"
            element={
              <Protected>
                <ReturnNew />
              </Protected>
            }
          />
          <Route
            path="/b2b/returns/credits"
            element={
              <Protected>
                <ReturnsMoney portal />
              </Protected>
            }
          />
          <Route
            path="/b2b/returns/:id"
            element={
              <Protected>
                <ReturnDetail />
              </Protected>
            }
          />
          <Route
            path="/refunds"
            element={
              <Protected>
                <Refunds />
              </Protected>
            }
          />

          {/* Purchasing */}
          {/* "Achats" — the purchases register (2026-10-13). It used to open the Comparateur a
              second time; the comparison stays at /purchasing/compare. */}
          <Route
            path="/purchasing"
            element={
              <Protected>
                <PurchasesRegister />
              </Protected>
            }
          />
          <Route
            path="/purchasing/purchase-import"
            element={
              <Protected>
                <PurchaseImport />
              </Protected>
            }
          />
          <Route
            path="/purchasing/alerts"
            element={
              <Protected>
                <StockAlerts />
              </Protected>
            }
          />
          <Route
            path="/purchasing/replenishment"
            element={
              <Protected>
                <Replenishment />
              </Protected>
            }
          />
          <Route
            path="/purchasing/compare"
            element={
              <Protected>
                <Comparison />
              </Protected>
            }
          />
          <Route
            path="/purchasing/orders"
            element={
              <Protected>
                <SupplierOrders />
              </Protected>
            }
          />
          <Route
            path="/purchasing/stock-import"
            element={
              <Protected>
                <StockImport />
              </Protected>
            }
          />
          <Route
            path="/purchasing/invoices"
            element={
              <Protected>
                <SupplierInvoiceOcr />
              </Protected>
            }
          />
          <Route
            path="/purchasing/returns"
            element={
              <Protected>
                <SupplierReturns />
              </Protected>
            }
          />

          {/* POS */}
          <Route
            path="/pos/sale"
            element={
              <Protected>
                <OrderCreate />
              </Protected>
            }
          />
          <Route
            path="/pos/close"
            element={
              <Protected>
                <PosClose />
              </Protected>
            }
          />
          <Route
            path="/pos/sessions"
            element={
              <Protected>
                <PosClose />
              </Protected>
            }
          />
          <Route
            path="/pos/tickets"
            element={
              <Protected>
                <OrdersList orderType="POS" titleKey="posTitle" descriptionKey="posDescription" />
              </Protected>
            }
          />

          {/* Customers + B2B */}
          <Route
            path="/customers"
            element={
              <Protected>
                <Customers />
              </Protected>
            }
          />
          <Route
            path="/customers/new"
            element={
              <Protected>
                <CustomerCreate />
              </Protected>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <Protected>
                <RelationDetail />
              </Protected>
            }
          />
          <Route
            path="/customers/:id/edit"
            element={
              <Protected>
                <CustomerCreate />
              </Protected>
            }
          />
          <Route
            path="/b2b/users"
            element={
              <Protected>
                <B2bUsers />
              </Protected>
            }
          />
          <Route
            path="/b2b/account"
            element={
              <Protected>
                <CustomerAccount />
              </Protected>
            }
          />
          <Route
            path="/b2b/dashboard"
            element={
              <Protected>
                <Dashboard />
              </Protected>
            }
          />

          {/* Admin */}
          <Route path="/admin" element={<Navigate to="/admin/settings" replace />} />
          <Route
            path="/admin/settings"
            element={
              <Protected>
                <Settings />
              </Protected>
            }
          />
          <Route
            path="/admin/counter-tabs"
            element={
              <Protected>
                <CounterTabs />
              </Protected>
            }
          />
          <Route
            path="/admin/document-templates"
            element={
              <Protected>
                <DocumentTemplates />
              </Protected>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <Protected>
                <Audit />
              </Protected>
            }
          />
          <Route
            path="/admin/users"
            element={
              <Protected>
                <Users />
              </Protected>
            }
          />
          <Route
            path="/admin/customers"
            element={
              <Protected>
                <Customers />
              </Protected>
            }
          />
          <Route
            path="/admin/suppliers"
            element={
              <Protected>
                <AdminSuppliers />
              </Protected>
            }
          />
          <Route
            path="/admin/pricing-grids"
            element={
              <Protected>
                <PricingGrids />
              </Protected>
            }
          />
          <Route
            path="/admin/brands"
            element={
              <Protected>
                <Brands />
              </Protected>
            }
          />
          {/* One supplier screen, two paths: the OWNER menu used to point here and links/
              bookmarks survive. The page gates itself on SYSTEM_SETTINGS_RW either way. */}
          <Route
            path="/suppliers"
            element={
              <Protected>
                <AdminSuppliers />
              </Protected>
            }
          />

          {/* Addendum v1.0 — Part J reporting + Part L e-invoicing */}
          <Route
            path="/reports"
            element={
              <Protected>
                <RevenueDashboard />
              </Protected>
            }
          />
          <Route
            path="/reports/revenue"
            element={
              <Protected>
                <RevenueDashboard />
              </Protected>
            }
          />
          <Route
            path="/reports/products"
            element={
              <Protected>
                <Stub k="reportProducts" />
              </Protected>
            }
          />
          <Route
            path="/reports/customers"
            element={
              <Protected>
                <Stub k="reportCustomers" />
              </Protected>
            }
          />
          <Route
            path="/reports/pos"
            element={
              <Protected>
                <Stub k="reportPos" />
              </Protected>
            }
          />
          <Route
            path="/reports/suppliers"
            element={
              <Protected>
                <Stub k="reportSuppliers" />
              </Protected>
            }
          />
          <Route
            path="/reports/export"
            element={
              <Protected>
                <Stub k="reportExport" />
              </Protected>
            }
          />
          <Route
            path="/einvoice"
            element={
              <Protected>
                <InvoiceBatchValidation />
              </Protected>
            }
          />
          <Route
            path="/einvoice/drafts"
            element={
              <Protected>
                <InvoiceBatchValidation />
              </Protected>
            }
          />
          <Route
            path="/einvoice/vat"
            element={
              <Protected>
                <VatReconciliation />
              </Protected>
            }
          />
          <Route
            path="/einvoice/monitoring"
            element={
              <Protected>
                <EInvoiceMonitoring />
              </Protected>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ToastProvider>
  );
}

export default App;
